/**
 * PostgreSQL Language Service Sync
 *
 * Handles communication with the PostgreSQL Language Server via IPC.
 * Manages document state, ordering, and LSP request/notification routing.
 */

import type {
  DiagnosticsListener,
  LanguageServiceDiagnosticsPayload,
  IntelliSenseStatus,
  IntelliSenseStatusPayload,
  IntelliSenseStatusListener,
} from './languageServiceProvider';
import { resolveSchemaReadyStatus, subscribeSchemaReadyStatus } from './introspectionTruncation';
// Ordering / de-duplication / rate-limiting mechanism for one document's
// traffic. Policy (what to send, when to recover) stays here.
import {
  clearDocumentSyncState,
  enqueueDocumentOp,
  getDesiredText,
  runRecoveryOnce,
  setDesiredText,
} from './pgDocumentSyncQueue';

interface DocumentState {
  uri: string;
  instanceKey: string;
  version: number;
  lastText: string;
  isOpenOnServer: boolean;
  /**
   * The document version the server was last told about — set only when a
   * `didOpen`/`didChange` actually reached it. `version` alone cannot stand in:
   * it also advances on the local-only branch, for edits the server never saw.
   * Diagnostics are matched against THIS.
   */
  serverVersion: number;
}

interface ActiveConnection {
  uri: string;
  connectionId: string;
  database: string;
  instanceKey: string;
}

const documentStates = new Map<string, DocumentState>();
const activeConnections = new Map<string, ActiveConnection>();
const diagnosticsListeners = new Set<DiagnosticsListener>();
let notificationListenerInitialized = false;

// IntelliSense status tracking
const documentStatus = new Map<string, IntelliSenseStatus>();
const intelliSenseStatusListeners = new Set<IntelliSenseStatusListener>();

const emitIntelliSenseStatus = (uri: string, status: IntelliSenseStatus, message?: string): void => {
  // S7 item 5 — a `ready` flip becomes `partial` (completion is live, minus
  // the artefacts an introspection row cap clipped) while a clip stands for
  // this document's (connectionId, database). Filtered here, at PG's single
  // emit choke point, so both readiness paths (the post-connect timer and the
  // reconnect path) stay honest without duplicating the check.
  let nextStatus = status;
  let nextMessage = message;
  if (status === 'ready') {
    const connection = activeConnections.get(uri);
    const resolved = resolveSchemaReadyStatus(connection?.connectionId, connection?.database);
    nextStatus = resolved.status;
    nextMessage = resolved.message ?? message;
  }

  documentStatus.set(uri, nextStatus);
  const payload: IntelliSenseStatusPayload = { uri, status: nextStatus, message: nextMessage };
  for (const listener of intelliSenseStatusListeners) {
    try {
      listener(payload);
    } catch (error) {
      console.error('[PgLanguageServiceSync] IntelliSense status listener error:', error);
    }
  }
};

// A clip or failure recorded AFTER a document went `ready` (phase 2 lands
// later than the readiness emit) must still reach the pill, and a clean
// retry must take it back — re-derive every affected document's status
// whenever the registry changes (release review 1.5.0, R2 / R3).
subscribeSchemaReadyStatus((connectionId, database) => {
  for (const [uri, connection] of activeConnections) {
    if (connection.connectionId !== connectionId) continue;
    if (connection.database.toLowerCase() !== database.toLowerCase()) continue;
    const current = documentStatus.get(uri);
    if (current !== 'ready' && current !== 'partial') continue;
    emitIntelliSenseStatus(uri, 'ready');
  }
});

export const onIntelliSenseStatus = (listener: IntelliSenseStatusListener): (() => void) => {
  intelliSenseStatusListeners.add(listener);
  return () => {
    intelliSenseStatusListeners.delete(listener);
  };
};

export const getIntelliSenseStatus = (uri: string): IntelliSenseStatus => {
  return documentStatus.get(uri) || 'unavailable';
};

/**
 * Creates an instance key from connection and database
 */
export const createInstanceKey = (connectionId: string, database: string): string => {
  return `${connectionId}:${database}`;
};

/**
 * Initializes the notification listener for diagnostics
 */
const initializeNotificationListener = (): void => {
  if (notificationListenerInitialized) {
    return;
  }

  if (!window.electronAPI?.pgsqlLanguageServer?.onNotification) {
    console.warn(
      '[PgLanguageServiceSync] pgsqlLanguageServer notifications API not available'
    );
    return;
  }

  notificationListenerInitialized = true;

  window.electronAPI.pgsqlLanguageServer.onNotification((instanceKey, method, params) => {
    try {
      // Handle diagnostics
      if (method === 'textDocument/publishDiagnostics') {
        const typedParams = params as { uri?: string; diagnostics?: unknown[]; version?: number };
        const uri = typedParams.uri;
        const diagnostics = typedParams.diagnostics || [];

        if (uri && typeof uri === 'string') {
          // Drop diagnostics computed for a buffer we have already replaced.
          // pgls echoes the document version it analysed (verified live: its
          // publishDiagnostics params are `{uri, diagnostics, version}`), and
          // it analyses buffers asynchronously — so under load a slow pass for
          // an OLD version can land after the new buffer's pass. Applying it
          // re-paints squiggles for text the user no longer has, and nothing
          // clears them until the next edit: the markers describe a buffer
          // that no longer exists. Equal versions still apply (a server may
          // publish more than once for one version).
          const publishedVersion = typedParams.version;
          const state = documentStates.get(uri);
          if (
            typeof publishedVersion === 'number' &&
            state !== undefined &&
            publishedVersion < state.serverVersion
          ) {
            return;
          }
          emitDiagnostics({ uri, diagnostics: diagnostics as LanguageServiceDiagnosticsPayload['diagnostics'] });
        }
      }

      // Log other notifications for debugging
      console.log(`[PgLanguageServiceSync] Received notification: ${method} for instance: ${instanceKey}`);
    } catch (error) {
      console.error('[PgLanguageServiceSync] Error handling notification:', error);
    }
  });
};

/**
 * Emits diagnostics to all registered listeners
 */
const emitDiagnostics = (payload: LanguageServiceDiagnosticsPayload): void => {
  for (const listener of diagnosticsListeners) {
    try {
      listener(payload);
    } catch (error) {
      console.error('[PgLanguageServiceSync] Diagnostics listener error:', error);
    }
  }
};

/**
 * Subscribes to diagnostics updates
 */
export const onDiagnostics = (listener: DiagnosticsListener): (() => void) => {
  initializeNotificationListener();
  diagnosticsListeners.add(listener);
  return () => {
    diagnosticsListeners.delete(listener);
  };
};

/**
 * Makes a request to the PostgreSQL Language Server
 */
const makeRequest = async (
  instanceKey: string,
  method: string,
  params: unknown,
  timeout = 10000
): Promise<unknown> => {
  if (!window.electronAPI?.pgsqlLanguageServer?.request) {
    throw new Error('PostgreSQL Language Server API is not available');
  }

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () => reject(new Error(`Request ${method} timed out after ${timeout}ms`)),
      timeout
    );
  });

  const requestPromise = window.electronAPI.pgsqlLanguageServer.request(
    instanceKey,
    method,
    params
  );

  const response = await Promise.race([requestPromise, timeoutPromise]);

  if (response.error) {
    throw new Error(response.error);
  }

  return response.result;
};

/**
 * Sends a notification to the PostgreSQL Language Server.
 *
 * Throws when the main process reports the notification never reached the
 * daemon. A notification has no response to wait on, so this is the ONLY
 * delivery signal — and `didOpen`/`didChange` carry the document sync, so
 * swallowing a drop would leave `lastText` claiming a dead daemon holds the
 * buffer and kill diagnostics for good. The message is one
 * `shouldAttemptReconnect` recognises, so the existing reconnect path picks
 * it up.
 */
const sendNotification = async (
  instanceKey: string,
  method: string,
  params: unknown
): Promise<void> => {
  if (!window.electronAPI?.pgsqlLanguageServer?.notify) {
    throw new Error('PostgreSQL Language Server API is not available');
  }

  const result = await window.electronAPI.pgsqlLanguageServer.notify(instanceKey, method, params);
  // Tolerate an older/shimmed preload that still resolves undefined — only an
  // explicit `delivered: false` is a drop.
  if (result && result.delivered === false) {
    throw new Error(result.error || 'Language server process exited');
  }
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

const shouldAttemptReconnect = (message: string): boolean => {
  const lower = message.toLowerCase();
  return (
    lower.includes('no postgresql language server instance') ||
    lower.includes('language server not initialized') ||
    lower.includes('language server process exited')
  );
};

/** Reconnect a document — single-flight and cooldown live in the queue module. */
const reconnectDocument = (uri: string): Promise<boolean> => {
  // NEVER recover while a context switch is in flight. `markAllDocumentsRestarting`
  // parks every document at `restarting` while the shared daemon is torn down
  // and rebuilt for another database. A reconnect started in that window races
  // the switch: `ensureDaemonContextFor` would see the half-switched context
  // and kick off a SECOND `switchContext`, so two switches fight over one
  // daemon and whichever lands last decides which schema every tab sees.
  //
  // This lives here rather than at the call sites because there are four of
  // them — the up-front update path, the post-failure retry, hover and
  // signature help — and only the first had the check. The switcher re-opens
  // these documents itself when it finishes.
  if (documentStatus.get(uri) === 'restarting') return Promise.resolve(false);
  return runRecoveryOnce(uri, () => reconnectDocumentUnguarded(uri));
};

/**
 * Make the shared daemon serve `(connectionId, database)` before we bind a
 * document to it. No-op when it already does, when the context is unknown, or
 * when the switch API is unavailable. Mirrors what `connectToDatabase` does on
 * the first connect; reused here so the RECOVERY path cannot silently rebind
 * the recorded context (see the call site).
 */
const ensureDaemonContextFor = async (
  uri: string,
  connectionId: string,
  database: string
): Promise<void> => {
  const api = window.electronAPI?.pgsqlLanguageServer;
  if (!api?.getContext || !api?.switchContext) return;
  try {
    const current = await api.getContext();
    if (!current) return;
    if (current.connectionId === connectionId && current.database === database) return;
    emitIntelliSenseStatus(uri, 'restarting', 'Switching database context...');
    markAllDocumentsRestarting();
    await api.switchContext(connectionId, database);
  } catch (error) {
    // A failed probe/switch must not abort recovery — `connectDocument` below
    // still reports the real outcome.
    console.warn('[PgLanguageServiceSync] Context check before reconnect failed:', error);
  }
};

const reconnectDocumentUnguarded = async (uri: string): Promise<boolean> => {
  const active = activeConnections.get(uri);
  if (!active) return false;

  if (!window.electronAPI?.languageServices?.connectDocument) {
    return false;
  }

  try {
    emitIntelliSenseStatus(uri, 'initializing', 'Reconnecting IntelliSense...');

    // The shared daemon serves ONE database at a time, and the main process's
    // `ensureInstance` overwrites its recorded `currentActiveDatabaseContext`
    // WITHOUT restarting the daemon. So reconnecting a document whose database
    // is not the current one must go through a real `switchContext` first —
    // otherwise the recorded context says one database while the daemon still
    // holds another's schema, `ensureCorrectContext` then sees a "matching"
    // context and never restarts, and that tab completes and diagnoses against
    // the wrong schema with nothing able to self-heal it.
    //
    // Switching (rather than refusing) is what keeps recovery working: a
    // document whose tab is active has nothing else coming to re-open it, so a
    // refusal here would leave it permanently without diagnostics.
    await ensureDaemonContextFor(uri, active.connectionId, active.database);

    const result = await window.electronAPI.languageServices.connectDocument({
      engine: 'pgsql',
      connectionId: active.connectionId,
      documentUri: uri,
      database: active.database,
    });

    if (!result.success) {
      emitIntelliSenseStatus(uri, 'error', getErrorMessage(result.error) || 'Failed to reconnect IntelliSense');
      return false;
    }

    // The tab may have closed while `connectDocument` was in flight. Re-adding
    // the document (and, below, its `activeConnections` entry) would resurrect
    // a server-side buffer for an editor that no longer exists.
    if (!activeConnections.has(uri)) return false;

    const instanceKey = result.instanceKey || createInstanceKey(active.connectionId, active.database);
    const state = documentStates.get(uri);

    if (state) {
      state.instanceKey = instanceKey;
      state.isOpenOnServer = false;
    } else {
      return false;
    }

    const opened = await openDocumentOnServer(uri, instanceKey);
    if (!opened) {
      emitIntelliSenseStatus(uri, 'error', 'Failed to reopen document for IntelliSense');
      return false;
    }

    activeConnections.set(uri, { ...active, instanceKey });
    emitIntelliSenseStatus(uri, 'ready');
    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Failed to reconnect document:', error);
    emitIntelliSenseStatus(uri, 'error', getErrorMessage(error));
    return false;
  }
};

/**
 * Registers a document with the language server
 */
export const registerDocument = async (
  uri: string,
  text: string,
  instanceKey?: string
): Promise<boolean> => {
  try {
    setDesiredText(uri, text);
    // Store initial state without instanceKey - will be set on connect
    documentStates.set(uri, {
      uri,
      instanceKey: instanceKey || '',
      version: 1,
      lastText: text,
      isOpenOnServer: false,
      serverVersion: 0,
    });
    // Document registered but not yet connected
    emitIntelliSenseStatus(uri, 'unavailable');

    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Failed to register document:', error);
    emitIntelliSenseStatus(uri, 'error', 'Failed to register document');
    return false;
  }
};

/**
 * Opens a document on the language server
 */
const openDocumentOnServer = async (uri: string, instanceKey: string): Promise<boolean> =>
  enqueueDocumentOp(uri, async () => {
    const state = documentStates.get(uri);
    if (!state) {
      console.error('[PgLanguageServiceSync] Document not registered:', uri);
      return false;
    }

    if (state.isOpenOnServer && state.instanceKey === instanceKey) {
      return true;
    }

    // Open with what the EDITOR currently holds, not with `lastText`. After a
    // failed send the two differ, and re-opening with `lastText` would hand
    // the server a buffer the user has already moved past — with no edit
    // pending to correct it.
    const text = getDesiredText(uri) ?? state.lastText;

    try {
      await sendNotification(instanceKey, 'textDocument/didOpen', {
        textDocument: {
          uri,
          languageId: 'sql',
          version: state.version,
          text,
        },
      });

      state.instanceKey = instanceKey;
      state.isOpenOnServer = true;
      state.lastText = text;
      state.serverVersion = state.version;
      return true;
    } catch (error) {
      console.error('[PgLanguageServiceSync] Failed to open document on server:', error);
      return false;
    }
  });

/**
 * Mark every document bound to `instanceKey` as no longer open on the server.
 *
 * Called when a notification to that instance is reported undelivered: the
 * daemon behind it is gone, so no document it was serving is really open any
 * more. Each affected document re-opens on its next update/hover instead of
 * silently talking to a server that has never seen it.
 */
const invalidateInstanceDocuments = (instanceKey: string): void => {
  if (!instanceKey) return;
  for (const state of documentStates.values()) {
    if (state.instanceKey === instanceKey) state.isOpenOnServer = false;
  }
};

/**
 * One `didChange` send. Runs inside the per-URI queue; re-reads the document
 * state from the map so a reconnect between attempts is picked up.
 */
const sendDidChange = async (uri: string, text: string): Promise<boolean> => {
  const state = documentStates.get(uri);
  if (!state) return false;

  try {
    // Skip if text hasn't changed
    if (state.lastText === text) {
      return true;
    }

    // If not connected to a language server, just update local state
    if (!state.isOpenOnServer || !state.instanceKey) {
      state.lastText = text;
      state.version++;
      return true;
    }

    const version = state.version + 1;
    state.version = version;

    const previousText = state.lastText;
    const previousLines = previousText.split(/\r\n|\r|\n/);
    const endLine = Math.max(previousLines.length - 1, 0);
    const endCharacter = previousLines[endLine]?.length ?? 0;

    await sendNotification(state.instanceKey, 'textDocument/didChange', {
      textDocument: {
        uri,
        version,
      },
      contentChanges: [
        {
          range: {
            start: { line: 0, character: 0 },
            end: { line: endLine, character: endCharacter },
          },
          text,
        },
      ],
    });

    state.lastText = text;
    state.serverVersion = version;
    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Failed to update document:', error);
    // The server did NOT get this buffer. Clearing the flag keeps `lastText`
    // honest (it still holds what the server last accepted) so the re-open
    // below, and any later update, resend rather than dedupe against text the
    // daemon never saw.
    //
    // …and the daemon that dropped it served EVERY document on this instance
    // key, not just this one. Its siblings still say `isOpenOnServer`, but a
    // respawned daemon has never seen them: their key is rebuilt from
    // `connectionId:database`, so it is byte-identical across a crash and
    // `openDocumentOnServer`'s "already open on this instance" early-return
    // would skip their `didOpen` forever. Their `didChange`s would then be
    // delivered to a server holding no such document — accepted at the pipe,
    // silently ignored, diagnostics dead with nothing to recover them.
    invalidateInstanceDocuments(state.instanceKey);
    return false;
  }
};

/**
 * Updates a document on the language server
 */
export const updateDocument = async (uri: string, text: string): Promise<boolean> => {
  const state = documentStates.get(uri);
  if (!state) {
    console.warn('[PgLanguageServiceSync] Document not registered, registering:', uri);
    await registerDocument(uri, text);
    return true;
  }

  // Record the editor's buffer BEFORE any await: every recovery path below
  // resyncs to this, so even an attempt that fails outright leaves the newest
  // text where the next re-open will find it.
  setDesiredText(uri, text);

  // A document that HAS a live connection but is no longer open on the server
  // lost its daemon. Re-open before sending — the same up-front guard hover
  // and signature help use. Without it `sendDidChange` would take its
  // local-only branch and report success forever: correct for a document that
  // has not connected YET (the pending didOpen carries the buffer), but for a
  // connected one it means the buffer silently stops reaching the server.
  //
  // (`reconnectDocument` itself refuses while a context switch is in flight.)
  if (!state.isOpenOnServer && activeConnections.has(uri)) {
    await reconnectDocument(uri);
  }

  // Queue updates to ensure ordering — the same queue `didOpen` rides, so the
  // two can never interleave (see `enqueueDocumentOp`).
  if (await enqueueDocumentOp(uri, () => sendDidChange(uri, text))) return true;

  // Delivery failed — the daemon died (or was replaced) under us. Re-open the
  // document on a fresh instance and resend, mirroring what hover and
  // signature help already do on a reconnect-worthy error. Without this the
  // buffer stays un-synced until something else happens to reconnect, and the
  // editor shows diagnostics for a buffer the user no longer has.
  //
  // The reconnect runs OUTSIDE the queue on purpose: `reconnectDocument`
  // re-opens through `openDocumentOnServer`, which enqueues on this same
  // per-URI queue — awaiting it from inside a queued op would deadlock.
  if (!(await reconnectDocument(uri))) return false;
  // Resend the CURRENT buffer, not the one this call captured: an edit that
  // arrived while we were failing has already overwritten `desiredText`, and
  // resending our own stale `text` would push the server backwards.
  return enqueueDocumentOp(uri, () => sendDidChange(uri, getDesiredText(uri) ?? text));
};

/**
 * Connects a document to the language server for a specific database
 */
export const connectToDatabase = async (
  documentUri: string,
  connectionId: string,
  database: string
): Promise<{ success: boolean; instanceKey?: string }> => {
  try {
    initializeNotificationListener();
    // Emit initializing status when connection starts
    emitIntelliSenseStatus(documentUri, 'initializing');

    if (!window.electronAPI?.languageServices?.connectDocument) {
      console.error(
        '[PgLanguageServiceSync] languageServices.connectDocument is not available'
      );
      emitIntelliSenseStatus(documentUri, 'error', 'Language services API not available');
      return { success: false };
    }

    // Check if we need to switch the PostgreSQL LSP context first
    // This handles the shared daemon limitation where only one database context can be active
    if (window.electronAPI?.pgsqlLanguageServer?.getContext && window.electronAPI?.pgsqlLanguageServer?.switchContext) {
      const currentContext = await window.electronAPI.pgsqlLanguageServer.getContext();

      if (currentContext && (currentContext.connectionId !== connectionId || currentContext.database !== database)) {
        console.log(`[PgLanguageServiceSync] Context switch needed: ${currentContext.connectionId}:${currentContext.database} -> ${connectionId}:${database}`);
        emitIntelliSenseStatus(documentUri, 'restarting', 'Switching database context...');

        // Mark other PG documents as restarting since daemon will restart
        markAllDocumentsRestarting();

        const switchResult = await window.electronAPI.pgsqlLanguageServer.switchContext(connectionId, database);
        if (!switchResult.success) {
          console.error('[PgLanguageServiceSync] Failed to switch context:', switchResult.error);
          emitIntelliSenseStatus(documentUri, 'error', 'Failed to switch database context');
          return { success: false };
        }

        console.log('[PgLanguageServiceSync] Context switch completed successfully');
        emitIntelliSenseStatus(documentUri, 'initializing', 'Connecting to database...');
      }
    }

    // Request the main process to ensure the language server instance exists
    const result = await window.electronAPI.languageServices.connectDocument({
      engine: 'pgsql',
      connectionId,
      documentUri,
      database,
    });

    if (!result.success) {
      console.error('[PgLanguageServiceSync] Failed to connect document:', result.error);
      emitIntelliSenseStatus(documentUri, 'error', 'Failed to connect to database');
      return { success: false };
    }

    const instanceKey = result.instanceKey || createInstanceKey(connectionId, database);

    // Ensure document is registered
    const state = documentStates.get(documentUri);
    if (!state) {
      await registerDocument(documentUri, '', instanceKey);
    }

    // Open document on the language server
    const opened = await openDocumentOnServer(documentUri, instanceKey);

    // Store connection info
    activeConnections.set(documentUri, {
      uri: documentUri,
      connectionId,
      database,
      instanceKey,
    });

    if (!opened) {
      // The didOpen never reached the daemon. Saying `ready` here would put a
      // green pill on a document the server does not have; the next edit's
      // recovery path re-opens it and flips the pill honestly.
      emitIntelliSenseStatus(documentUri, 'error', 'Failed to open document for IntelliSense');
      return { success: false };
    }

    // PostgreSQL Language Server is typically ready quickly after connection
    // Set to ready after a short delay (schema introspection is fast)
    setTimeout(() => {
      const currentStatus = documentStatus.get(documentUri);
      if (currentStatus === 'initializing') {
        emitIntelliSenseStatus(documentUri, 'ready');
      }
    }, 2000);

    return { success: true, instanceKey };
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error connecting to database:', error);
    emitIntelliSenseStatus(documentUri, 'error', 'Error connecting to database');
    return { success: false };
  }
};

/**
 * Disconnects a document from the language server
 */
export const disconnectDatabase = async (documentUri: string): Promise<boolean> => {
  try {
    const connection = activeConnections.get(documentUri);
    const state = documentStates.get(documentUri);

    if (state?.isOpenOnServer && state.instanceKey) {
      try {
        await sendNotification(state.instanceKey, 'textDocument/didClose', {
          textDocument: { uri: documentUri },
        });
      } catch (error) {
        console.warn('[PgLanguageServiceSync] Error sending didClose:', error);
      }
    }

    if (connection && window.electronAPI?.languageServices?.disconnectDocument) {
      await window.electronAPI.languageServices.disconnectDocument({
        engine: 'pgsql',
        documentUri,
        instanceKey: connection.instanceKey,
      });
    }

    activeConnections.delete(documentUri);
    if (state) {
      state.isOpenOnServer = false;
    }

    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error disconnecting from database:', error);
    return false;
  }
};

/**
 * Gets hover information from the language server
 */
export const getHoverInfo = async (
  uri: string,
  line: number,
  character: number
): Promise<unknown | null> => {
  let state = documentStates.get(uri);
  if (!state?.isOpenOnServer || !state.instanceKey) {
    const reconnected = await reconnectDocument(uri);
    if (reconnected) {
      state = documentStates.get(uri);
    }
  }

  if (!state?.isOpenOnServer || !state.instanceKey) {
    return null;
  }

  try {
    const requestHover = async (instanceKey: string) => {
      return makeRequest(
        instanceKey,
        'textDocument/hover',
        {
          textDocument: { uri },
          position: { line, character },
        },
        5000
      );
    };

    try {
      return await requestHover(state.instanceKey);
    } catch (error) {
      const message = getErrorMessage(error);
      if (shouldAttemptReconnect(message) && (await reconnectDocument(uri))) {
        const nextState = documentStates.get(uri);
        if (nextState?.isOpenOnServer && nextState.instanceKey) {
          return await requestHover(nextState.instanceKey);
        }
      }
      throw error;
    }
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error getting hover info:', error);
    return null;
  }
};

/**
 * Gets signature help from the language server
 */
export const getSignatureHelp = async (
  uri: string,
  line: number,
  character: number
): Promise<unknown | null> => {
  let state = documentStates.get(uri);
  if (!state?.isOpenOnServer || !state.instanceKey) {
    const reconnected = await reconnectDocument(uri);
    if (reconnected) {
      state = documentStates.get(uri);
    }
  }

  if (!state?.isOpenOnServer || !state.instanceKey) {
    return null;
  }

  try {
    const requestSignatureHelp = async (instanceKey: string) => {
      return makeRequest(
        instanceKey,
        'textDocument/signatureHelp',
        {
          textDocument: { uri },
          position: { line, character },
        },
        5000
      );
    };

    try {
      return await requestSignatureHelp(state.instanceKey);
    } catch (error) {
      const message = getErrorMessage(error);
      if (shouldAttemptReconnect(message) && (await reconnectDocument(uri))) {
        const nextState = documentStates.get(uri);
        if (nextState?.isOpenOnServer && nextState.instanceKey) {
          return await requestSignatureHelp(nextState.instanceKey);
        }
      }
      throw error;
    }
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error getting signature help:', error);
    return null;
  }
};

/**
 * Closes a document
 */
export const closeDocument = async (uri: string): Promise<boolean> => {
  try {
    if (hasActiveConnection(uri)) {
      await disconnectDatabase(uri);
    }

    documentStates.delete(uri);
    documentStatus.delete(uri);
    // Drops the op queue, the desired-text record and any in-flight/cooling
    // recovery together, so a reconnect already on the wire cannot re-open a
    // closed tab's buffer and the maps do not grow for the session's life.
    clearDocumentSyncState(uri);

    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error closing document:', error);
    return false;
  }
};

/**
 * Checks if a document has an active connection
 */
export const hasActiveConnection = (uri: string): boolean => {
  return activeConnections.has(uri);
};

/**
 * Gets the active connection for a document
 */
export const getActiveConnection = (
  uri: string
): { connectionId: string; database: string; instanceKey: string } | null => {
  const connection = activeConnections.get(uri);
  return connection
    ? {
        connectionId: connection.connectionId,
        database: connection.database,
        instanceKey: connection.instanceKey,
      }
    : null;
};

/**
 * Ensures the PostgreSQL LSP daemon is running with the correct database context.
 * Called when switching to an existing tab to handle the shared daemon limitation.
 * Returns true if context is correct (or was switched successfully), false on error.
 */
export const ensureCorrectContext = async (
  connectionId: string,
  database: string
): Promise<boolean> => {
  if (!window.electronAPI?.pgsqlLanguageServer?.getContext || !window.electronAPI?.pgsqlLanguageServer?.switchContext) {
    return true; // API not available, assume OK
  }

  try {
    const currentContext = await window.electronAPI.pgsqlLanguageServer.getContext();

    // If no context set, or context matches, we're good
    if (!currentContext || (currentContext.connectionId === connectionId && currentContext.database === database)) {
      return true;
    }

    // Need to switch context
    console.log(`[PgLanguageServiceSync] Tab switch requires context change: ${currentContext.connectionId}:${currentContext.database} -> ${connectionId}:${database}`);

    // Mark all documents as restarting
    markAllDocumentsRestarting();

    const switchResult = await window.electronAPI.pgsqlLanguageServer.switchContext(connectionId, database);
    if (!switchResult.success) {
      console.error('[PgLanguageServiceSync] Failed to switch context on tab change:', switchResult.error);
      return false;
    }

    console.log('[PgLanguageServiceSync] Context switch on tab change completed successfully');

    // Mark documents as initializing - they will reconnect on next use
    markAllDocumentsInitializing();
    return true;
  } catch (error) {
    console.error('[PgLanguageServiceSync] Error ensuring correct context:', error);
    return false;
  }
};

/**
 * Marks all active PostgreSQL documents as 'restarting'.
 * Called by pgContextSwitcher when the daemon is being restarted for a database context switch.
 */
export const markAllDocumentsRestarting = (): void => {
  for (const [uri] of activeConnections) {
    emitIntelliSenseStatus(uri, 'restarting', 'Switching database context...');
    // Mark document as not open on server since we're restarting
    const state = documentStates.get(uri);
    if (state) {
      state.isOpenOnServer = false;
    }
  }
};

/**
 * Marks all active PostgreSQL documents back to 'initializing'.
 * Called after daemon restart completes, documents will reconnect automatically.
 */
export const markAllDocumentsInitializing = (): void => {
  for (const [uri] of activeConnections) {
    emitIntelliSenseStatus(uri, 'initializing', 'Reconnecting to database...');
  }
};
